import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  GraduationCap,
  ClipboardCheck,
  Users,
  BarChart3,
} from "lucide-react";
import "./EDU568.scss";

// ============================================================================
// DATA
// ============================================================================

type Cycle = "monthly" | "annual";

interface Tier {
  key: string;
  name: string;
  blurb: string;
  students: string;
  monthly: number;
  annual: number;
  cta: string;
  highlighted?: boolean;
  features: string[];
}

const TIERS: Tier[] = [
  {
    key: "starter",
    name: "Starter",
    blurb: "For single schools and small academies",
    students: "Up to 500 students",
    monthly: 99,
    annual: 79,
    cta: "Start free trial",
    features: [
      "Enrollment & course registration",
      "Gradebook & transcripts",
      "Student & parent portal",
      "Email support",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    blurb: "For colleges and growing institutions",
    students: "Up to 10,000 students",
    monthly: 349,
    annual: 279,
    cta: "Start free trial",
    highlighted: true,
    features: [
      "Everything in Starter",
      "Degree audit & academic planning",
      "Financial aid & billing integration",
      "Attendance & advising workflows",
      "Custom reporting & dashboards",
      "Priority support",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    blurb: "For universities and multi-campus systems",
    students: "Unlimited students & campuses",
    monthly: 0,
    annual: 0,
    cta: "Talk to sales",
    features: [
      "Multi-campus & cross-college enrollment",
      "SIS, LMS & ERP integrations",
      "SSO & role-based access controls",
      "Dedicated implementation manager",
      "99.95% uptime SLA",
      "Custom data residency & compliance",
    ],
  },
];

const FEATURES = [
  {
    icon: GraduationCap,
    photo: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80&auto=format&fit=crop",
    title: "Enrollment that doesn't fall apart at scale",
    body: "Course registration, waitlists, and prerequisite checks run automatically, so add/drop week doesn't turn into a support queue for your registrar's office.",
  },
  {
    icon: ClipboardCheck,
    photo: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=800&q=80&auto=format&fit=crop",
    title: "One record, from admission to transcript",
    body: "Grades, attendance, financial aid, and advising notes live on a single student record, so staff stop reconciling data across five disconnected systems.",
  },
  {
    icon: BarChart3,
    photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80&auto=format&fit=crop",
    title: "Retention signals before students disappear",
    body: "Attendance drops, missed assignments, and at-risk grade patterns surface to advisors automatically, so outreach happens before a student withdraws.",
  },
];

const STORIES = [
  {
    company: "Marlowe Community College",
    industry: "Community College",
    metric: "Registration time cut from days to minutes",
    quote:
      "Add/drop week used to mean a line out the registrar's door. Students self-serve through the portal now, and our staff handle exceptions instead of every transaction.",
    person: "Registrar",
  },
  {
    company: "Aldergate University",
    industry: "4-Year University",
    metric: "First-year retention up 9 points",
    quote:
      "Advisors get a flag the moment a student's attendance or grades slip, instead of finding out at the end of the semester. That early signal is the whole reason retention improved.",
    person: "VP of Student Success",
  },
  {
    company: "Brightwell School District",
    industry: "K-12 District",
    metric: "12 schools on one system",
    quote:
      "We ran twelve separate gradebooks before this. Parents and teachers now see the same record, in real time, across every school in the district.",
    person: "Director of Technology",
  },
];

const FAQS = [
  {
    q: "Can it integrate with our existing LMS?",
    a: "Yes. The platform integrates with major LMS providers including Canvas, Blackboard, and Moodle, syncing grades, rosters, and assignments automatically.",
  },
  {
    q: "Does it support multi-campus institutions?",
    a: "Yes. Growth and Enterprise plans support cross-campus enrollment, shared course catalogs, and a consolidated reporting dashboard across every campus.",
  },
  {
    q: "What does onboarding and data migration look like?",
    a: "Starter and Growth include guided self-serve setup with student record import from your previous SIS. Enterprise plans include a dedicated implementation manager and staff training.",
  },
  {
    q: "How is student data protected?",
    a: "Data is encrypted in transit and at rest, with FERPA-aligned access controls. Enterprise plans add SSO, role-based permissions, and custom data residency options.",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const EDU568: React.FC = () => {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Every CTA on this page routes here.
  const goToSignup = () => navigate("/auth/signup");

  return (
    <div className="wrap">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">Academic Portal</span>
            <h1>One portal for enrollment, grades, and student success</h1>
            <p className="hero-sub">
              Enrollment, gradebook, advising, and financial aid in a single
              system — built for institutions who are tired of stitching
              together a SIS, an LMS, and spreadsheets that never agree.
            </p>
            <div className="hero-cta">
              <button className="btn-primary" onClick={goToSignup}>
                Start free trial <ArrowRight size={16} />
              </button>
              <button className="btn-ghost" onClick={goToSignup}>Book a demo</button>
            </div>
            <p className="hero-fineprint">
              No credit card required &nbsp;·&nbsp; Free for 14 days &nbsp;·&nbsp; Cancel anytime
            </p>
          </div>

          <div className="hero-art">
            <img
              className="hero-photo"
              src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200&q=80&auto=format&fit=crop"
              alt="Students studying together on campus"
            />
            <div className="hero-photo-card">
              <span className="hero-photo-card-label">Enrolled this term</span>
              <span className="hero-photo-card-value">4,218</span>
              <span className="hero-photo-card-sub">+6.4% vs last term</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Platform</span>
          <h2>Everything an institution needs, from admission to graduation</h2>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div className="feature-card" key={f.title}>
              <div className="feature-photo-wrap">
                <img className="feature-photo" src={f.photo} alt="" />
                <span className="feature-icon">
                  <f.icon size={18} strokeWidth={1.75} />
                </span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing">
        <div className="section-head">
          <span className="eyebrow">Pricing</span>
          <h2>Plans built around your enrollment</h2>
          <p className="section-sub">
            All plans include the full enrollment, gradebook, and student portal suite.
          </p>
          <div className="toggle">
            <button
              className={cycle === "monthly" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={cycle === "annual" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("annual")}
            >
              Annual <span className="toggle-save">Save 20%</span>
            </button>
          </div>
        </div>

        <div className="pricing-grid">
          {TIERS.map((tier) => (
            <div
              className={tier.highlighted ? "tier tier-highlight" : "tier"}
              key={tier.key}
            >
              {tier.highlighted && <span className="tier-badge">Most popular</span>}
              <h3>{tier.name}</h3>
              <p className="tier-blurb">{tier.blurb}</p>

              <div className="tier-volume">
                <span className="tier-volume-value">{tier.students}</span>
                <span className="tier-volume-label">per institution</span>
              </div>

              <div className="tier-price">
                {tier.monthly === 0 ? (
                  <span className="price-custom">Custom pricing</span>
                ) : (
                  <>
                    <span className="price-amount">
                      ${cycle === "monthly" ? tier.monthly : tier.annual}
                    </span>
                    <span className="price-period">/ mo</span>
                  </>
                )}
              </div>

              <button
                className={tier.highlighted ? "btn-primary full" : "btn-secondary full"}
                onClick={goToSignup}
              >
                {tier.cta}
              </button>
              <ul className="tier-features">
                {tier.features.map((f) => (
                  <li key={f}>
                    <Check size={15} strokeWidth={2.25} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CLIENT SUCCESS STORIES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Customer stories</span>
          <h2>Results from institutions running on the platform</h2>
        </div>
        <div className="story-grid">
          {STORIES.map((s) => (
            <div className="story-card" key={s.company}>
              <div className="story-metric">
                <TrendingUp size={16} strokeWidth={2} />
                {s.metric}
              </div>
              <p className="story-quote">&ldquo;{s.quote}&rdquo;</p>
              <div className="story-attrib">
                <div className="story-company">{s.company}</div>
                <div className="story-role">
                  {s.person} &middot; {s.industry}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY STRIP */}
      <section className="security">
        <div className="security-item">
          <ShieldCheck size={18} strokeWidth={1.75} />
          FERPA-aligned access controls
        </div>
        <div className="security-item">
          <Users size={18} strokeWidth={1.75} />
          SSO & role-based permissions
        </div>
        <div className="security-item">
          <BarChart3 size={18} strokeWidth={1.75} />
          99.95% uptime SLA
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section">
        <div className="section-head">
          <span className="eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <div className="faq-item" key={item.q}>
              <button
                className="faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <ChevronDown
                  size={18}
                  className={openFaq === i ? "faq-chev open" : "faq-chev"}
                />
              </button>
              {openFaq === i && <p className="faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-band">
        <h2>See your institution running on the platform</h2>
        <p>Set up takes about twenty minutes. No credit card required.</p>
        <div className="hero-cta center">
          <button className="btn-primary" onClick={goToSignup}>
            Start free trial <ArrowRight size={16} />
          </button>
          <button className="btn-ghost on-dark" onClick={goToSignup}>Book a demo</button>
        </div>
      </section>
    </div>
  );
};

export default EDU568;